import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import OptimizedImage from './OptimizedImage';
import { preloadImages, preloadNextImages } from '../lib/imagePreloader';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './ImageCarousel.css';

/**
 * ImageCarousel component that displays a carousel of images using Swiper
 * 
 * @param {Object} props Component props
 * @param {Array<string|Object>} props.images Array of image paths or image objects with src and alt
 * @param {string} props.title Title for the carousel (for accessibility)
 * @returns {React.ReactElement} ImageCarousel component
 */
const ImageCarousel = ({ images, title }) => {
  // We're tracking the active index for preloading purposes
  const [, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  
  // Preload images when component mounts
  useEffect(() => {
    if (!images || images.length === 0) return;
    
    setLoading(true);
    
    // Preload the first few images
    preloadImages(images.slice(0, 3), (progress) => {
      setProgress(progress * 100);
    }).then(() => {
      setLoading(false);
      
      // Preload the rest in the background
      preloadImages(images.slice(3));
    });
  }, [images]);
  
  // Handle slide change
  const handleSlideChange = (swiper) => {
    setActiveIndex(swiper.activeIndex);
    
    // Preload next images
    preloadNextImages(images, swiper.activeIndex);
  };
  return (
    <div className="rounded-2xl overflow-hidden shadow-card">
      {loading && (
        <div className="flex items-center justify-center bg-gray-100 aspect-[3/2]">
          <div className="w-full max-w-md">
            <div className="bg-gray-200 rounded-full h-2.5 mb-4">
              <div 
                className="bg-brand-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-center text-sm text-gray-500">Loading images...</p>
          </div>
        </div>
      )}
      
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={0}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={true}
        className={`w-full ${loading ? 'opacity-0 h-0' : 'opacity-100'}`}
        aria-label={`${title} image carousel`}
        onSlideChange={handleSlideChange}
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <OptimizedImage 
              src={typeof image === 'string' ? image : image.src} 
              alt={typeof image === 'string' ? `${title} - image ${index + 1}` : image.alt} 
              className="mx-auto" /* Center the image */
              style={{ 
                maxHeight: '70vh', /* Limit height to 70% of viewport height */
                maxWidth: '100%', /* Ensure image doesn't overflow container width */
                objectFit: 'contain' /* Maintain aspect ratio */
              }}
              lazy={false} /* Don't lazy load carousel images */
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string.isRequired
      })
    ])
  ).isRequired,
  title: PropTypes.string.isRequired
};

export default ImageCarousel;
